import { PdfGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GTuple, GLabelType, GLabel } from '../../pdf/glabel';
import { genMiniLuk, drawMiniLuk, MiniLukData } from '../../pdf/board/miniLuk';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/label';

/**
 * MiniLUK (Label version)
 *
 * Layout: Landscape A4 with MiniLUK puzzle format.
 * 12 items arranged in a 2×6 grid: answers (top) and questions (bottom).
 * Mini tiles in the middle show the self-checking solution pattern.
 * Items are grouped by answer so players match questions to answers.
 *
 * Migrated from C# EduFrame.Single.Generators.MiniLUK__Base / MiniLUK__Skupiny
 */

function labelText(label: GLabel): string {
  return label?.type === GLabelType.Text ? (label.getText() ?? '') : '';
}

abstract class MiniLukBase implements ILayoutLabelGen {
  protected abstract readonly counts: Set<number>;

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage(true); // landscape

    let itemIndex = 0;
    const items = genMiniLuk<GTuple>(
      () => {
        const tuple = opts.list[itemIndex % opts.list.length];
        itemIndex++;
        return tuple;
      },
      (tuple) => labelText(tuple.right), // group by answer
      (tuple) => labelText(tuple.left), // unique by question
      this.counts,
      opts.data.random
    );

    let font = GUtil.baseFont;

    drawMiniLuk(
      page,
      items,
      // Draw question (bottom grid)
      (data: MiniLukData<GTuple>) => {
        page.drawLabel(data.obj.left, { rect: data.rect, font });
      },
      // Draw answer (top grid)
      (data: MiniLukData<GTuple>) => {
        page.drawLabel(data.obj.right, { rect: data.rect, font });
      },
      // Adjust font once width is known
      (width: number) => {
        const texts = items.map((i) => labelText(i.obj.left)).filter(Boolean);
        if (texts.length > 0) {
          font = page.adjustFont(width, texts, GUtil.baseFont);
        }
      },
      opts.data
    );
  }
}

/**
 * MiniLUK__Base — counts [12, 6, 4, 3, 2]: allows 1 item per group (12 groups)
 * up to 6 items per group (2 groups).
 */
export class MiniLUK__Base extends MiniLukBase implements IItemCount {
  countDefault(): number {
    return 12;
  }
  countMin(): number {
    return 12;
  }
  countMax(): number {
    return 12;
  }

  protected readonly counts = new Set([12, 6, 4, 3, 2]);
}
